import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  createInspection,
  fetchInspectionById,
  fetchInspections,
  lookupHIN,
  normalizeHIN,
  type InspectionFilters,
} from './api'
import { BOATS_KEY } from '@/features/inventory/hooks'
import type { InspectionCreateBody } from '@/types/inspection'

export const INSPECTIONS_KEY = ['inspections'] as const

export const useInspections = (filters: InspectionFilters = {}) =>
  useQuery({
    queryKey:
      Object.keys(filters).length > 0 ? [...INSPECTIONS_KEY, filters] : [...INSPECTIONS_KEY],
    queryFn: () => fetchInspections(filters),
  })

export const useInspection = (id: string) =>
  useQuery({ queryKey: [...INSPECTIONS_KEY, id], queryFn: () => fetchInspectionById(id) })

// Inspections attached to one work order (the intake that fed it).
export const useInspectionsForWorkOrder = (workOrderId: string) =>
  useQuery({
    queryKey: [...INSPECTIONS_KEY, { workOrderId }],
    queryFn: () => fetchInspections({ workOrderId }),
  })

// Inspections not attached to any work order — the same pool the Inspection
// page lists, and the one the boat-create form can adopt from.
export const useAdoptableInspections = () =>
  useQuery({
    queryKey: [...INSPECTIONS_KEY, { unattached: true }],
    queryFn: () => fetchInspections({ unattached: true }),
  })

// Auto-fill lookup, keyed by the normalized HIN so typing/deleting refetches.
// Only fires once the input could plausibly be a full HIN (12-14 chars).
export const useHINLookup = (hin: string) => {
  const normalized = normalizeHIN(hin)
  return useQuery({
    queryKey: ['hin-lookup', normalized],
    queryFn: () => lookupHIN(normalized),
    enabled: normalized.length >= 12,
    staleTime: Infinity,
    retry: false,
  })
}

export const useCreateInspection = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: InspectionCreateBody) => createInspection(body),
    onSuccess: (inspection) => {
      qc.invalidateQueries({ queryKey: INSPECTIONS_KEY })
      // The linked boat gained engine data (or was newly created).
      qc.invalidateQueries({ queryKey: BOATS_KEY })
      qc.invalidateQueries({ queryKey: [...BOATS_KEY, inspection.boat_id] })
    },
  })
}
